import { getDefaultStore } from "jotai";
import { seatGatedAtom } from "../stores/seatGate";
import { onServerDown } from "../lib/serverWake";

export const API_BASE = "/api/v1";

// ApiError preserves the backend's HTTP status + optional `code`
// string so callers can distinguish structured signals (like
// `github_token_expired`) from generic failures without parsing the
// message. Plain `Error` was hiding those codes and forcing callers
// to do ad-hoc regex matching.
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) return false;

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    localStorage.setItem("token", data.access_token);
    return true;
  } catch {
    return false;
  }
}

// request() is the bare HTTP layer — auth header + JSON parsing + 401
// refresh. It deliberately does NOT inject any galaxy/account header.
//
// Galaxy scope is the URL's job. Each caller is expected to put the
// account on the path itself (`?account_login=:account` via the
// withAccount() helper or an equivalent embedded query param). Reading
// it from localStorage here was the structural galaxy-bleed bug — the
// atom could lag the URL during navigation, post-login redirect, or
// deep-link entry, and the BE's AccountResolver would happily resolve
// the wrong galaxy. Removing the implicit header forces every call site
// to make scope explicit. See feedback_galaxy_url_truth.
export async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (res.status === 401) {
    // Try refresh token (deduplicate concurrent refreshes)
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = tryRefreshToken().finally(() => { isRefreshing = false; });
    }
    const refreshed = await refreshPromise;
    if (refreshed) {
      // Retry original request with new token
      const newToken = localStorage.getItem("token");
      const retryRes = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
          ...options?.headers,
        },
      });
      if (retryRes.ok) {
        if (retryRes.status === 204 || retryRes.headers.get("content-length") === "0") {
          return undefined as T;
        }
        return retryRes.json();
      }
    }
    // Refresh failed — logout
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    // The BE's structured code lives in `error` (ErrorResponse.Error); some
    // older paths use `code`. Read both so callers' e.code checks work for
    // either shape.
    const code = error.code ?? error.error;
    // Seat gate: a private-mode galaxy whose observations need a paid seat
    // returns 402 needs_seat on every endpoint. Flip the global flag so the
    // app shows the full-page SeatGate instead of broken/empty views.
    if (res.status === 402 && code === "needs_seat") {
      getDefaultStore().set(seatGatedAtom, true);
    }
    // Server down (gateway/unavailable/timeout) — the whole backend is
    // unreachable, e.g. stg scaled to 0 during the idle window. Flip the global
    // waking flag (ServerWakingOverlay confirms via /health + reconnects) and, on
    // stg, ping the failover origin to trip the wake Lambda. NOT 500: that is an
    // app error for the caller to surface, not a "server waking" state.
    if (res.status === 502 || res.status === 503 || res.status === 504) {
      onServerDown();
    }
    throw new ApiError(error.message || res.statusText, res.status, code);
  }

  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

// DisclosureLevel mirrors the BE's D-05 three-step ladder (json
// `disclosure` on every galaxy-view member row). "observation" is the
// fail-closed default for a member who has NOT opted in — anonymised and
// unscored, but still an active member, not "excluded".
export type DisclosureLevel = "full" | "context" | "observation";

// requireAccount asserts that the caller supplied a non-empty galaxy.
// Empty strings are the bleed path: the BE's AccountResolver falls back
// to the JWT's account_id, which is the user's personal galaxy and not
// whichever galaxy the user is currently looking at. Throwing here makes
// the failure loud at dev time instead of silently rendering wrong-galaxy
// data. Every page-level fetch must derive `accountLogin` from
// useParams().account so URL stays the source of truth.
export function requireAccount(accountLogin: string, where: string): string {
  if (!accountLogin) {
    throw new Error(
      `${where}: accountLogin is required and must be derived from useParams().account ` +
        `(galaxy URL). Empty string would let the BE resolve a different galaxy from JWT.`,
    );
  }
  return accountLogin;
}

// withAccount appends ?account_login=:galaxy to a galaxy-scoped path.
// The query param wins over the X-Account-Login header on the BE — see
// AccountResolver middleware for the precedence rule.
export function withAccount(path: string, accountLogin: string, extraParams?: Record<string, string>): string {
  const params = new URLSearchParams();
  params.set("account_login", accountLogin);
  for (const [k, v] of Object.entries(extraParams || {})) params.set(k, v);
  return `${path}?${params.toString()}`;
}

// DEV-ONLY reveal-all switch (toggled in DevMenu, persisted in localStorage).
// When on, galaxy reads carry ?dev_reveal_all=true so the backend bypasses
// disclosure — but ONLY in stg/local: the server gates it on DevToolsEnabled(),
// so the param is silently ignored in production.
export const DEV_REVEAL_ALL_KEY = "dev_reveal_all";
export function devRevealAll(): boolean {
  try {
    return localStorage.getItem(DEV_REVEAL_ALL_KEY) === "1";
  } catch {
    return false;
  }
}
